import React, { useMemo, useState, useEffect } from 'react';
import {
	TranslatedString,
	LocallySortedAndPaginatedTable,
	makeLocallySortedAndPaginatedTableColumnHeader,
	useAjax,
	XhrStatus,
	Card
} from '@bosch/bt-ui';
import './WebConnectMetricsPage.css';
import withErrorHandler from '../../components/ErrorHandler';
import RestfulCallout from '../../components/RestfulCallout';
import RestfulCallLoading from '../../components/RestfulCallLoading';
import ContentDialog from '../../components/ContentDialog';

const TABLE_HEADERS = [
	makeLocallySortedAndPaginatedTableColumnHeader('ContainerName', 'Container Name'),
	makeLocallySortedAndPaginatedTableColumnHeader('ConnectionCount', 'Connection Count'),
	makeLocallySortedAndPaginatedTableColumnHeader('CPUUsage', 'CPU Usage'),
	makeLocallySortedAndPaginatedTableColumnHeader('MemoryUsage', 'Memory Usage'),
	makeLocallySortedAndPaginatedTableColumnHeader('UpdateDate', 'Update Date')
];

const METRICA_DETAIL_TABLE_HEADERS = [
	makeLocallySortedAndPaginatedTableColumnHeader('CloudId', 'CloudId'),
	makeLocallySortedAndPaginatedTableColumnHeader('Host', 'Host'),
	makeLocallySortedAndPaginatedTableColumnHeader('Port', 'Port'),
	makeLocallySortedAndPaginatedTableColumnHeader('ICCID', 'ICCID'),
	makeLocallySortedAndPaginatedTableColumnHeader('TransportType', 'Transport'),
	makeLocallySortedAndPaginatedTableColumnHeader('Protocol', 'Protocol'),
	makeLocallySortedAndPaginatedTableColumnHeader('ConnectedTime', 'Connected Time'),
	makeLocallySortedAndPaginatedTableColumnHeader('LastSendDataTime', 'Last Send Time'),
	makeLocallySortedAndPaginatedTableColumnHeader('LastReceivedTime', 'Last Received Time'),
	makeLocallySortedAndPaginatedTableColumnHeader('TransactionID', 'TransactionID'),
];

function getMetrics(getWCMetrics, token) {
	getWCMetrics('GET', SUPPORTCONSOLE_API_URL + '/api/wcmetrics/v1/metrics/getall', undefined, undefined, { 'Authorization': 'Bearer ' + token.token });
}

function getConnections(getWCConnections, token, containerName) {
	getWCConnections(
		'GET',
		SUPPORTCONSOLE_API_URL + '/api/wcmetrics/v1/metrics/' + encodeURIComponent(containerName) + '/connections',
		undefined,
		undefined,
		{ 'Authorization': 'Bearer ' + token.token }
	);
}

function DatetimeFormatConverter(dateTime) {
	if (!dateTime) {
		return '';
	}

	const trimmedDate = dateTime.trim();
	const withTimeDelimiter = trimmedDate.includes('T')
		? trimmedDate
		: trimmedDate.replace(' ', 'T');

	const hasTimeZoneSuffix = /([+-]\d{2}:\d{2}|Z)$/i.test(withTimeDelimiter);
	const isoDate = hasTimeZoneSuffix ? withTimeDelimiter : `${withTimeDelimiter}Z`;

	const localDate = new Date(isoDate);
	if (Number.isNaN(localDate.getTime())) {
		return trimmedDate;
	}

	const year = localDate.getFullYear();
	const month = String(localDate.getMonth() + 1).padStart(2, '0');
	const day = String(localDate.getDate()).padStart(2, '0');
	const hours = String(localDate.getHours()).padStart(2, '0');
	const minutes = String(localDate.getMinutes()).padStart(2, '0');
	return `${year}/${month}/${day} ${hours}:${minutes}`;
}

function mapConnectionRows(datas) {
	return (datas ?? []).map(data => {
		const {
			transactionId,
			connectionId,
			connectedTime,
			lastReceivedTime,
			lastSendDataTime,
			cloudId,
			ipAddress,
			portNumber,
			iccid,
			transportType,
			relayProtocol
		} = data;

		return {
			key: connectionId,
			columns: [
				{ key: 'CloudId', columnContent: cloudId },
				{ key: 'Host', columnContent: ipAddress },
				{ key: 'Port', columnContent: portNumber },
				{ key: 'ICCID', columnContent: iccid },
				{ key: 'TransportType', columnContent: transportType },
				{ key: 'Protocol', columnContent: relayProtocol },
				{ key: 'ConnectedTime', columnContent: DatetimeFormatConverter(connectedTime) },
				{ key: 'LastSendDataTime', columnContent: DatetimeFormatConverter(lastSendDataTime) },
				{ key: 'LastReceivedTime', columnContent: DatetimeFormatConverter(lastReceivedTime) },
				{ key: 'TransactionID', columnContent: transactionId }
			],
			htmlAttributes: undefined,
			subRows: undefined
		};
	});
}

const WebConnectMetricsPage = (props) => {
	const {
		token,
	} = props;

	const [currentPage, setCurrentPage] = useState(1);
	const [openDialog, setOpenDialog] = useState(false);
	const [detailRows, setDetailRows] = useState();
	const [selectedContainerName, setSelectedContainerName] = useState();
	const [result, status, getWCMetrics, response] = useAjax(XhrStatus.InProgress);
	const [connectionsResult, connectionsStatus, getWCConnections, connectionsResponse] = useAjax();

	useEffect(
		() => {
			// If useAjax(XhrStatus.InProgress) and not login yet, it will show 401
			// So we need to make sure token is avaliable to continue...
			if (token?.token) {
				getMetrics(getWCMetrics, token);
			}
		},
		[getWCMetrics, token]
	);

	useEffect(
		() => {
			if (!selectedContainerName || connectionsStatus === XhrStatus.InProgress || connectionsStatus === XhrStatus.Initial) {
				return;
			}

			if (connectionsStatus === XhrStatus.Finished && connectionsResult?.status === 'Success') {
				setDetailRows(mapConnectionRows(connectionsResult?.data));
				setOpenDialog(true);
				setSelectedContainerName(undefined);
			} else if (connectionsStatus === XhrStatus.Error || connectionsStatus === XhrStatus.Finished) {
				setSelectedContainerName(undefined);
			}
		},
		[connectionsResult, connectionsStatus, selectedContainerName]
	);

	function makeRow(task) {
		const {
			containerName,
			connectionCount,
			cpuUsage,
			memoryUsage,
			updateDate
		} = task;

		return {
			key: containerName,
			columns: [
				{
					key: 'ContainerName',
					columnContent: containerName
				},
				{
					key: 'ConnectionCount',
					columnContent: connectionCount
				},
				{
					key: 'CPUUsage',
					columnContent: cpuUsage + ' %'
				},
				{
					key: 'MemoryUsage',
					columnContent: memoryUsage + ' MB'
				},
				{
					key: 'UpdateDate',
					columnContent: DatetimeFormatConverter(updateDate)
				}
			],
			htmlAttributes: {
				onClick: () => {
					if (!token?.token || connectionsStatus === XhrStatus.InProgress) {
						return;
					}
					setSelectedContainerName(containerName);
					getConnections(getWCConnections, token, containerName);
				}
			},
			subRows: undefined
		};
	}

	const rows = useMemo(
		() => {
			console.log(result);
			return (result?.data) ? result.data.map(item => makeRow(item)) : [];
		},
		[result, token, connectionsStatus, getWCConnections]
	);

	const loadingStatus = status === XhrStatus.InProgress || connectionsStatus === XhrStatus.InProgress
		? XhrStatus.InProgress
		: status;

	return (
		<Card
			htmlAttributes={{
				id: 'wc-metrics-page'
			}}
		>
			<RestfulCallout status={status} response={response} returnObj={result} hideSuccess={true} />
			<RestfulCallout status={connectionsStatus} response={connectionsResponse} returnObj={connectionsResult} hideSuccess={true} />
			{
				<LocallySortedAndPaginatedTable
					headers={TABLE_HEADERS}
					rows={rows}
					numRowsPerPage={100}
					currentPage={currentPage}
					goToPageFunc={setCurrentPage}
					htmlAttributes={{
						className: 'wc-metrics-table'
					}}
					errorMessage="Error occured"
					noResultsMessage={<TranslatedString stringId="No Metrics Data found" />}
				/>
			}
			{
				<ContentDialog
					openDialog={openDialog}
					setOpenDialog={setOpenDialog}
					tableHeaders={METRICA_DETAIL_TABLE_HEADERS}
					tableRows={detailRows}
					numRowsPerPage={50}
				/>
			}
			<RestfulCallLoading status={loadingStatus} />
		</Card>
	);
};

export default withErrorHandler(React.memo(WebConnectMetricsPage));
